import {
  all,
  call,
  cancel,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects'
import { buffers, delay, eventChannel } from 'redux-saga'

import { REMOVE_CREDENTIALS } from 'modules/auth/actions'
import actions, {
  INIT,
  TEARDOWN,
  MAKE_CALL,
  HANGUP,
  TOGGLE_MUTE,
} from './actions'
import { VOICE_CONFIG, warnVoiceConfigOnce } from './lib/config'
import {
  backoffDelay,
  callDisabledReason,
  canUseVoice,
  causeFromGateway,
  dialTarget,
  isAuthCloseCode,
  isDialableSipUser,
  isOnline,
  mediaErrorKind,
  shouldHandleGatewayHangup,
} from './lib/helpers'
import {
  GatewaySocket,
  fetchSipCredentials,
  fetchVehicles,
} from './lib/gatewayClient'
import SipClient from './lib/sipClient'
import { getOrCreateSessionId } from './lib/sessionId'
import { startDialTone, stopDialTone } from './lib/dialtone'

// Error policy (the same for REST and the WebSocket):
// - 401/403 from the gateway: the token is rejected. Stop the whole voice
//   session and show one message. Never retry, so an expired or foreign token
//   cannot turn into a request loop against Laravel behind the gateway. A
//   refusal before the gateway ever took the token (an account that may not
//   use voice) stops quietly instead, as 'notPermitted'.
// - 5xx, 404, network errors and dropped sockets: retry with bounded
//   exponential backoff (backoffDelay).
// Every part runs under a try/catch or a race, so nothing in here can throw
// into the app's root saga (a single all([fork(...)])).

// A REGISTER that is neither accepted nor rejected within this time (or a
// registration lost while idle) is treated as a dead connection.
const REGISTER_TIMEOUT_MS = 20000

// A gateway socket open at least this long counts as healthy.
const STABLE_CONNECTION_MS = 30000

const CAUSE_NOTICES = {
  busy: ['warning', 'voiceCauseBusy'],
  'no-answer': ['warning', 'voiceCauseNoAnswer'],
  unavailable: ['warning', 'voiceCauseUnavailable'],
  declined: ['warning', 'voiceCauseDeclined'],
  failed: ['error', 'voiceCauseFailed'],
}

const parkForever = () => new Promise(() => {})

const selectVoice = state => state.voice

function isAuthenticated(auth) {
  return !!(auth && auth.done && auth.accessToken && auth.profile)
}

function getOrCreateAudioElement() {
  const id = 'voice-remote-audio'
  let el = document.getElementById(id)
  if (!el) {
    el = document.createElement('audio')
    el.id = id
    el.autoplay = true
    el.setAttribute('playsinline', 'true')
    el.style.display = 'none'
    document.body.appendChild(el)
  }
  return el
}

function* endActiveCall(cause) {
  stopDialTone()
  const { activeCall } = yield select(selectVoice)
  if (!activeCall) return
  yield put(actions.callEnded(cause))
  const notice = cause ? CAUSE_NOTICES[cause] : null
  if (notice) yield put(actions.notify(notice[0], notice[1]))
}

// --------------------------------------------------------------------------
// Gateway: presence over /ws/user
// --------------------------------------------------------------------------

function createSocketChannel(socket) {
  return eventChannel(emit => {
    socket.setListener(emit)
    socket.connect()
    return () => socket.close()
  }, buffers.expanding(16))
}

function* handleGatewayMessage(msg) {
  const type = msg && msg.type
  if (type === 'presence_update') {
    yield put(actions.presenceUpdate(msg.payload))
  } else if (type === 'call_hangup') {
    const { activeCall } = yield select(selectVoice)
    if (shouldHandleGatewayHangup(msg.payload, activeCall)) {
      yield call(endActiveCall, causeFromGateway(msg.payload.cause))
    }
  }
  // incoming_call / call_answered are ignored: outbound calls only. Vehicles
  // cannot reach an admin browser yet, so nothing may ring here.
}

// Returns { stop: 'unauthorized' | 'fatal' }. Retries everything else.
// Sets session.accepted once the gateway takes the token.
function* runGateway(token, session) {
  let attempt = 0
  let notified = false
  while (true) {
    yield put(
      actions.setGatewayState(attempt === 0 ? 'connecting' : 'retrying'),
    )
    const chan = createSocketChannel(
      new GatewaySocket(VOICE_CONFIG.gatewayWsUrl, token),
    )
    let closed = null
    let openedAt = 0
    try {
      while (!closed) {
        const ev = yield take(chan)
        if (ev.type === 'open') {
          openedAt = Date.now()
          session.accepted = true
          yield put(actions.setGatewayState('connected'))
        } else if (ev.type === 'message') {
          yield call(handleGatewayMessage, ev.data)
        } else if (ev.type === 'close') {
          closed = ev
        }
      }
    } finally {
      chan.close()
    }

    // Only a connection that stayed up resets the backoff. One that drops
    // right after opening (e.g. a gateway that kicks the same admin's other
    // tab) keeps backing off instead of reconnecting every two seconds.
    if (openedAt && Date.now() - openedAt >= STABLE_CONNECTION_MS) {
      attempt = 0
      notified = false
    }
    yield put(actions.setGatewayState('retrying'))
    if (closed.fatal) return { stop: 'fatal' }
    if (isAuthCloseCode(closed.code)) return { stop: 'unauthorized' }

    // A refused upgrade (401 or 503) reaches the browser only as close code
    // 1006, so ask the REST endpoint which one it was. A good answer also
    // refreshes presence while the socket is down.
    const probe = yield call(fetchVehicles, VOICE_CONFIG.gatewayUrl, token)
    if (!probe.ok && probe.kind === 'unauthorized') {
      return { stop: 'unauthorized' }
    }
    if (probe.ok) {
      session.accepted = true
      yield put(actions.presenceUpdate(probe.data))
    } else if (!notified) {
      notified = true
      yield put(actions.notify('warning', 'voiceErrGatewayUnavailable'))
    }
    attempt += 1
    yield call(delay, backoffDelay(attempt))
  }
}

// --------------------------------------------------------------------------
// SIP: credentials, registration, reconnection
// --------------------------------------------------------------------------

function createSipChannel(sip) {
  return eventChannel(emit => {
    sip.setListener(emit)
    return () => sip.setListener(null)
  }, buffers.expanding(16))
}

function createSipClient(creds) {
  return new SipClient({
    wsUrl: VOICE_CONFIG.sipWsUrl,
    ext: creds.ext,
    password: creds.password,
    realm: creds.realm,
    iceServers: VOICE_CONFIG.iceServers,
    iceGatheringTimeout: VOICE_CONFIG.iceGatheringTimeout,
    audioElement: getOrCreateAudioElement(),
  })
}

// One connection of the session's SipClient. Returns why it ended:
// { kind: 'rejected', statusCode } | { kind: 'connect-failed' } | { kind: 'lost' }
function* runSipClient(sip, sipRef, progress) {
  const chan = createSipChannel(sip)
  sipRef.current = sip
  try {
    try {
      // SimpleUser construction (bad URL) also throws in here.
      yield call([sip, sip.connect])
    } catch (err) {
      console.warn('[voice] SIP connect failed:', err && err.message)
      return { kind: 'connect-failed' }
    }

    while (true) {
      const { sipState } = yield select(selectVoice)
      let ev
      if (sipState === 'registered') {
        ev = yield take(chan)
      } else {
        const result = yield race({
          ev: take(chan),
          timeout: call(delay, REGISTER_TIMEOUT_MS),
        })
        if (result.timeout) {
          // Never tear down a live call over a registration hiccup.
          if (sip.hasActiveCall()) continue
          return { kind: 'lost' }
        }
        ev = result.ev
      }

      switch (ev.type) {
        case 'registered':
          progress.registered = true
          yield put(actions.setSipState('registered'))
          break
        case 'unregistered':
          yield put(actions.setSipState('connecting'))
          break
        case 'register-rejected':
          return { kind: 'rejected', statusCode: ev.statusCode }
        case 'server-disconnect':
          return { kind: 'lost' }
        case 'call-answered':
          stopDialTone()
          yield put(actions.callAnswered())
          break
        case 'call-hangup':
          yield call(endActiveCall, ev.cause)
          break
        default:
          break
      }
    }
  } finally {
    chan.close()
    if (sipRef.current === sip) sipRef.current = null
    yield call([sip, sip.disconnect])
  }
}

// Returns { stop: 'unauthorized' | 'fatal' } when the session must end.
// A REGISTER rejected with 401/403 parks with sipState 'failed' instead: the
// gateway presence keeps working, and retrying the same credentials would
// only be refused again.
// Credentials are fetched once, so one SipClient serves the whole session and
// every reconnect reuses it (see SipClient on why a new one per retry leaks).
function* runSip(token, sessionId, sipRef, session) {
  let creds = null
  let sip = null
  let attempt = 0
  let notifiedGateway = false
  let notifiedSip = false

  try {
    while (true) {
      if (!creds) {
        const res = yield call(
          fetchSipCredentials,
          VOICE_CONFIG.gatewayUrl,
          token,
          sessionId,
        )
        if (!res.ok && res.kind === 'unauthorized') {
          return { stop: 'unauthorized' }
        }
        if (!res.ok && res.kind === 'invalid') {
          return { stop: 'fatal' }
        }
        if (!res.ok) {
          yield put(
            actions.setSipState(
              'retrying',
              `gateway ${res.status || 'unreachable'}`,
            ),
          )
          if (!notifiedGateway) {
            notifiedGateway = true
            yield put(actions.notify('warning', 'voiceErrGatewayUnavailable'))
          }
          attempt += 1
          yield call(delay, backoffDelay(attempt))
          continue
        }
        creds = res.data
        session.accepted = true
        attempt = 0
      }
      if (!sip) sip = createSipClient(creds)

      yield put(actions.setSipState('connecting'))
      const progress = { registered: false }
      const outcome = yield call(runSipClient, sip, sipRef, progress)

      // The transport may drop mid-call; its hangup event then never arrives.
      yield call(endActiveCall, outcome.kind === 'lost' ? 'failed' : null)

      const code = outcome.kind === 'rejected' ? outcome.statusCode : null
      if (code === 401 || code === 403) {
        yield put(actions.setSipState('failed', `REGISTER ${code}`))
        yield put(
          actions.notify('error', 'voiceStatusSipFailed', {
            detail: `SIP ${code}`,
          }),
        )
        yield call(parkForever)
      }

      yield put(
        actions.setSipState(
          'retrying',
          code ? `REGISTER ${code}` : outcome.kind,
        ),
      )
      if (!notifiedSip) {
        notifiedSip = true
        yield put(
          actions.notify(
            'warning',
            code ? 'voiceStatusSipFailed' : 'voiceErrSipUnreachable',
            { detail: code ? `SIP ${code}` : null },
          ),
        )
      }

      if (progress.registered) {
        attempt = 0
        notifiedSip = false
      }
      attempt += 1
      yield call(delay, backoffDelay(attempt))
    }
  } finally {
    if (sip) yield call([sip, sip.dispose])
  }
}

// --------------------------------------------------------------------------
// UI commands
// --------------------------------------------------------------------------

function* makeCallWorker(sipRef, action) {
  try {
    const vehicle = action.payload.vehicle
    const { sipState, activeCall } = yield select(selectVoice)
    const sip = sipRef.current
    // Read once, so the value checked is the value dialled.
    const target = dialTarget(vehicle)
    // The UI already hides/disables the button in these cases.
    if (!sip || !isOnline(vehicle) || !isDialableSipUser(target)) return
    if (callDisabledReason({ sipState, activeCall, vehicle })) return

    yield put(actions.callDialing(vehicle))
    startDialTone()
    try {
      yield call([sip, sip.call], target)
    } catch (err) {
      yield call(endActiveCall, null)
      const notice = dialFailureNotice(err, sip.localHangup)
      if (notice) yield put(notice)
    }
  } catch (err) {
    console.error('[voice] make call crashed', err)
  }
}

// The INVITE could not be sent. Microphone problems get their own message;
// a hang-up before the INVITE went out (e.g. during the microphone prompt)
// is not an error.
function dialFailureNotice(err, hungUpByUser) {
  const media = mediaErrorKind(err)
  if (media === 'denied') {
    return actions.notify('error', 'voiceErrMicDenied', {
      hintKey: 'voiceErrMicDeniedHint',
    })
  }
  if (media === 'notFound') {
    return actions.notify('error', 'voiceErrMicNotFound')
  }
  if (media === 'unavailable') {
    return actions.notify('error', 'voiceErrMicUnavailable')
  }
  if (hungUpByUser) return null
  console.warn('[voice] dial failed:', err && err.message)
  return actions.notify('error', 'voiceErrDialFailed')
}

function* hangupWorker(sipRef) {
  try {
    stopDialTone()
    const sip = sipRef.current
    let sent = false
    if (sip) {
      try {
        yield call([sip, sip.hangup])
        sent = true
      } catch (err) {
        // No SIP session left (already ended): just clear the UI.
      }
    }
    // When sent, the SIP hangup event ends the call in the store.
    if (!sent) yield call(endActiveCall, null)
  } catch (err) {
    console.error('[voice] hangup crashed', err)
  }
}

function* toggleMuteWorker(sipRef) {
  try {
    const { mute, activeCall } = yield select(selectVoice)
    const sip = sipRef.current
    if (!sip || !activeCall || activeCall.state !== 'active') return
    const applied = sip.setMute(!mute)
    if (applied) yield put(actions.muteChanged(!mute))
  } catch (err) {
    console.warn('[voice] mute failed:', err && err.message)
  }
}

function* watchCommands(sipRef) {
  yield all([
    takeEvery(MAKE_CALL, makeCallWorker, sipRef),
    takeEvery(HANGUP, hangupWorker, sipRef),
    takeEvery(TOGGLE_MUTE, toggleMuteWorker, sipRef),
  ])
}

// --------------------------------------------------------------------------
// Session
// --------------------------------------------------------------------------

function* runVoiceSession(token) {
  const sipRef = { current: null }
  // accepted: the gateway took the token at least once (socket open, or a
  // REST answer) during this session.
  const session = { accepted: false }
  try {
    yield put(actions.sessionStarted())
    const sessionId = getOrCreateSessionId()
    const result = yield race({
      gateway: call(runGateway, token, session),
      sip: call(runSip, token, sessionId, sipRef, session),
      commands: call(watchCommands, sipRef),
    })
    const stop = (result.gateway || result.sip || {}).stop
    if (stop === 'unauthorized' && !session.accepted) {
      // Refused on first contact. The gateway answers 401 alike for a bad
      // token and for an account that may not use voice (a CPAC customer, an
      // inactive user), and a token that had already expired is the rest of
      // the app's business. So no "sign in again" error: stop quietly and
      // let isVoiceUsable hide calling.
      yield put(actions.sessionStopped('notPermitted'))
    } else if (stop === 'unauthorized') {
      yield put(actions.sessionStopped('unauthorized'))
      yield put(actions.notify('error', 'voiceStatusUnauthorized'))
    } else {
      yield put(actions.sessionStopped('error'))
      yield put(actions.notify('error', 'voiceStatusStopped'))
    }
  } finally {
    stopDialTone()
  }
}

function* guardedSession(token) {
  try {
    yield call(runVoiceSession, token)
  } catch (err) {
    console.error('[voice] session crashed', err)
    try {
      yield put(actions.sessionStopped('error'))
      yield put(actions.notify('error', 'voiceStatusStopped'))
    } catch (ignored) {}
  }
}

function* voiceLifecycle() {
  while (true) {
    yield take(INIT)
    const auth = yield select(state => state.auth)
    // A customer account would only be refused; isVoiceUsable hides calling
    // for it.
    if (!isAuthenticated(auth) || !canUseVoice(auth.profile)) continue
    const task = yield fork(guardedSession, auth.accessToken)
    const stop = yield take([TEARDOWN, REMOVE_CREDENTIALS])
    yield cancel(task)
    if (stop.type === REMOVE_CREDENTIALS) yield put(actions.teardown())
  }
}

export default function* watchVoiceState() {
  warnVoiceConfigOnce()
  if (!VOICE_CONFIG.enabled) return
  try {
    yield call(voiceLifecycle)
  } catch (err) {
    console.error('[voice] stopped after an unexpected error', err)
  }
}
